/**
 * Окно «Выбрать / Сменить картинку кадра» (видео-ролик из готового кадра, §5/§10 спеки
 * `2026-10-13-frame-to-video-design.md`): стилл кадра — любой готовый вариант из «Результатов»
 * (генерация, доработка, улучшение, загрузка); клип, видео-вариант и незавершённая генерация не
 * годятся — тот же гейт, что у «Сделать видео» (`canMakeVideo`). «Снять ссылку» возвращает кадр к
 * прежнему поведению — `picked`-варианту его собственных генераций (`videoclip.still_of`).
 *
 * Заголовок и подсказка — по состоянию кадра (задача Оли №308): у кадра без картинки это не «сменить»,
 * а «взять готовый кадр из Результатов и не делать генерацию вообще» (её же слова, №311, №312), и
 * сказать о бесплатности здесь важнее, чем назвать действие.
 *
 * По образцу `fitting/DressPickerDialog.tsx`: плитка превью, «Показать ещё» по курсору страницы.
 */
import { useInfiniteQuery } from "@tanstack/react-query";

import { RetryImg } from "@/components/trends/PinGrid";
import { Btn, Empty, ErrorLine, Hint } from "@/components/project/ui";
import { Dialog } from "@/components/trends/Dialog";
import { canMakeVideo } from "@/lib/frameVideo";
import { thumbUrl } from "@/lib/media";
import { errorText } from "@/lib/project";
import { loadResults } from "@/lib/results";
import { useSetStill } from "@/lib/video";

export function ShotStillPicker({
  open,
  onClose,
  pid,
  shotId,
  currentVariantId,
}: {
  open: boolean;
  onClose: () => void;
  pid: number;
  shotId: number;
  /** Текущая ссылка стилла кадра (`shot_still_variant_id`); `null` — ссылки нет. */
  currentVariantId: number | null;
}) {
  const setStill = useSetStill(pid);
  const feed = useInfiniteQuery({
    queryKey: ["shot-still-picker"],
    queryFn: ({ pageParam }) => loadResults({ status: "done" }, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (p) => p.next_before ?? undefined,
    enabled: open,
  });
  const gens = (feed.data?.pages ?? [])
    .flatMap((p) => p.gens)
    .filter((g) => g.variant_id !== null && canMakeVideo(g.kind, g.status, g.video_ms != null).ok);

  const pick = (variantId: number | null) => setStill.mutate({ sid: shotId, variantId }, { onSuccess: onClose });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={currentVariantId === null ? "Выбрать картинку кадра" : "Сменить картинку кадра"}
    >
      <div className="grid gap-2 p-4" data-annot="видео: сменить картинку кадра">
        <Hint>
          Любое готовое изображение из «Результатов» — не клип, не видео, не в очереди и не с ошибкой. Выбор
          бесплатный: кадр берётся как есть, генерация не запускается.
        </Hint>
        {currentVariantId !== null ? (
          <Btn tone="ghost" disabled={setStill.isPending} onClick={() => pick(null)}>
            Снять ссылку — вернуть стилл по умолчанию
          </Btn>
        ) : null}
        <ErrorLine text={setStill.isError ? errorText(setStill.error) : ""} />
        {feed.isPending ? (
          <Hint>Загружаю…</Hint>
        ) : gens.length === 0 ? (
          <Empty>Готовых изображений пока нет.</Empty>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-2">
            {gens.map((g) => (
              <button
                key={g.variant_id}
                type="button"
                disabled={setStill.isPending}
                onClick={() => pick(g.variant_id!)}
                title={`вариант #${g.variant_id}`}
                className={`relative overflow-hidden rounded-[4px] border ${
                  g.variant_id === currentVariantId ? "border-accent ring-1 ring-accent" : "border-border hover:border-border-strong"
                }`}
              >
                <RetryImg src={thumbUrl(g.asset_path)} alt="" className="aspect-[3/4] w-full object-cover" />
              </button>
            ))}
          </div>
        )}
        {feed.isError ? <ErrorLine text={errorText(feed.error)} /> : null}
        {feed.hasNextPage ? (
          <Btn disabled={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
            {feed.isFetchingNextPage ? "Загружаю…" : "Показать ещё"}
          </Btn>
        ) : null}
      </div>
    </Dialog>
  );
}
